import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  MIGRATION_BATCH_STATUSES,
  MigrationBatchSchema,
  type MigrationBatchDto,
} from '@taqon/contracts';
import { useNavigate, useSearchParams } from 'react-router';
import { getPage, postJson } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { FormDialog, StatusChip } from '../../shared/ui/forms';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { Pager } from '../crm/common';
import { RecordLink } from '../finance/detail';
import { BATCHES, STATUS_LABELS, sampleHref, templateHref, useImporters, when } from './common';
import { SearchFilterBar } from '../../shared/ui/filters';

const PAGE_SIZE = 50;

/**
 * Master-data migration batches (FRD §15; SDD §21.2): each batch loads one target record type from
 * one approved source file. Filters and the page are server-side and kept in the URL.
 */
export function BatchesPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, 'migration.batch:create');
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [creating, setCreating] = useState(false);
  const importers = useImporters();
  // Import on a master-data page arrives here with its record type (CLR-59, FRD-MIG-037).
  const preset = canCreate ? (params.get('new') ?? '') : '';
  const status = params.get('status') ?? '';
  const target = params.get('target') ?? '';
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const set = (key: 'status' | 'target' | 'page', value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };

  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(PAGE_SIZE),
    sort: '-created_at',
  });
  if (status !== '') query.set('filter[status]', status);
  if (target !== '') query.set('filter[target_record_type]', target);
  const batches = useQuery({
    queryKey: ['migration', 'batches', query.toString()],
    queryFn: () => getPage(`${BATCHES}?${query.toString()}`, MigrationBatchSchema),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Migration batches"
        description="Loads of approved master data: staged, validated by a dry run, approved, applied, reconciled and locked."
        actions={
          canCreate ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={(event) => {
                event.currentTarget.focus();
                setCreating(true);
              }}
            >
              New batch
            </Button>
          ) : undefined
        }
      />
      <SearchFilterBar aria-label="Batch filters">
        <TextField
          select
          size="small"
          label="Status"
          // "All" is the empty value; without these the select would show it blank.
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          value={status}
          onChange={(e) => {
            set('status', e.target.value);
          }}
          sx={{ minWidth: 200 }}
        >
          <MenuItem value="">All</MenuItem>
          {MIGRATION_BATCH_STATUSES.map((s) => (
            <MenuItem key={s} value={s}>
              {STATUS_LABELS[s] ?? s}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Target"
          value={target}
          onChange={(e) => {
            set('target', e.target.value);
          }}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All</MenuItem>
          {(importers.data ?? []).map((i) => (
            <MenuItem key={i.recordType} value={i.recordType}>
              {i.label}
            </MenuItem>
          ))}
        </TextField>
      </SearchFilterBar>
      {batches.isError && <ErrorState message={batches.error.message} />}
      <Section>
        {batches.isPending && <LoadingState label="Loading batches" />}
        {batches.data?.items.length === 0 && <EmptyState title="No migration batches found" />}
        {batches.data !== undefined && batches.data.items.length > 0 && (
          <>
            <BatchTable batches={batches.data.items} />
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={batches.data.total}
              noun="batch(es)"
              onPage={(next) => {
                set('page', String(next));
              }}
            />
          </>
        )}
      </Section>
      {(creating || preset !== '') && (
        <NewBatchDialog
          initialTarget={preset}
          onClose={(created) => {
            setCreating(false);
            if (created !== undefined) {
              void navigate(`/migration/batches/${created.id}`);
            } else if (preset !== '') {
              const next = new URLSearchParams(params);
              next.delete('new');
              setParams(next);
            }
          }}
        />
      )}
    </Stack>
  );
}

function BatchTable({ batches }: { batches: readonly MigrationBatchDto[] }) {
  return (
    <Table size="small" aria-label="Migration batches">
      <TableHead>
        <TableRow>
          <TableCell>Batch</TableCell>
          <TableCell>Target</TableCell>
          <TableCell>Source</TableCell>
          <TableCell>Status</TableCell>
          <TableCell align="right">Rows</TableCell>
          <TableCell align="right">Accepted</TableCell>
          <TableCell align="right">Rejected</TableCell>
          <TableCell align="right">Out of scope</TableCell>
          <TableCell align="right">Control total</TableCell>
          <TableCell>Created</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {batches.map((batch) => (
          <TableRow key={batch.id} hover>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>
              <RecordLink to={`/migration/batches/${batch.id}`}>{batch.batchNumber}</RecordLink>
            </TableCell>
            <TableCell>{batch.targetLabel}</TableCell>
            <TableCell sx={{ overflowWrap: 'anywhere' }}>{batch.sourceReference}</TableCell>
            <TableCell>
              <StatusChip status={batch.status} />
            </TableCell>
            <TableCell align="right">{batch.itemCount}</TableCell>
            <TableCell align="right">{batch.acceptedCount}</TableCell>
            <TableCell align="right">{batch.rejectedCount}</TableCell>
            <TableCell align="right">{batch.outOfScopeCount}</TableCell>
            <TableCell align="right">{batch.controlRowCount}</TableCell>
            <TableCell sx={{ whiteSpace: 'nowrap' }}>{when(batch.createdAt)}</TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

/**
 * Opens a batch: the target record type (with its CSV template), the approved source reference
 * and the control total the source states. The file is uploaded on the batch's page.
 */
function NewBatchDialog({
  initialTarget = '',
  onClose,
}: {
  /** The record type chosen already: Import on a master-data page (CLR-59). */
  initialTarget?: string;
  onClose: (created?: MigrationBatchDto) => void;
}) {
  const session = useSession();
  const queryClient = useQueryClient();
  const importers = useImporters();
  const [target, setTarget] = useState(initialTarget);
  const [source, setSource] = useState('');
  const [control, setControl] = useState('');
  const importer = importers.data?.find((i) => i.recordType === target);
  const controlValid = /^\d{1,7}$/.test(control.trim());

  const save = useMutation({
    mutationFn: () =>
      postJson(
        BATCHES,
        {
          targetRecordType: target,
          sourceReference: source.trim(),
          controlRowCount: Number.parseInt(control.trim(), 10),
        },
        MigrationBatchSchema,
        session.data?.csrfToken ?? '',
      ),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['migration', 'batches'] });
      onClose(created);
    },
  });

  return (
    <FormDialog
      open
      restoreFocus={initialTarget === ''}
      title="New migration batch"
      submitLabel="Create batch"
      canSubmit={target !== '' && source.trim() !== '' && controlValid}
      submitting={save.isPending}
      error={save.isError ? save.error.message : null}
      onClose={() => {
        onClose();
      }}
      onSubmit={() => {
        save.mutate();
      }}
    >
      {importers.isError && <ErrorState message={importers.error.message} />}
      <TextField
        select
        required
        label="Target record type"
        value={target}
        onChange={(e) => {
          setTarget(e.target.value);
        }}
      >
        {(importers.data ?? []).map((i) => (
          <MenuItem key={i.recordType} value={i.recordType}>
            {i.label}
          </MenuItem>
        ))}
      </TextField>
      {importer !== undefined && (
        <Stack spacing={1}>
          <Alert severity="info" variant="outlined">
            {importer.notes} The key column is <strong>{importer.keyColumn}</strong>.
          </Alert>
          <Table size="small" aria-label="CSV columns">
            <TableHead>
              <TableRow>
                <TableCell>Column</TableCell>
                <TableCell>Required</TableCell>
                <TableCell>Description</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {importer.columns.map((c) => (
                <TableRow key={c.name}>
                  <TableCell sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                    {c.name}
                  </TableCell>
                  <TableCell>{c.required ? 'Yes' : 'No'}</TableCell>
                  <TableCell>
                    <Typography variant="body2">{c.description}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Stack direction="row" spacing={3} sx={{ flexWrap: 'wrap' }}>
            <Link
              href={templateHref(importer)}
              download={`${importer.recordType}-template.csv`}
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
            >
              <FileDownloadIcon fontSize="small" /> Download template CSV
            </Link>
            {sampleHref(importer) !== undefined && (
              <Link
                href={sampleHref(importer)}
                download={`${importer.recordType}-sample.csv`}
                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
              >
                <FileDownloadIcon fontSize="small" /> Download sample CSV
              </Link>
            )}
          </Stack>
        </Stack>
      )}
      <TextField
        label="Source reference"
        required
        value={source}
        onChange={(e) => {
          setSource(e.target.value);
        }}
        helperText="The approved source: its file name and any note identifying it."
        slotProps={{ htmlInput: { maxLength: 500 } }}
      />
      <TextField
        label="Control total (rows in the source)"
        required
        value={control}
        onChange={(e) => {
          setControl(e.target.value);
        }}
        error={control !== '' && !controlValid}
        helperText="The number of rows the approved source states; reconciliation compares to it."
        slotProps={{ htmlInput: { inputMode: 'numeric' } }}
      />
    </FormDialog>
  );
}
